import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Search, 
  FolderOpen,
  Calendar,
  DollarSign,
  TrendingUp,
  Trash2,
  Pencil,
  PenTool,
  Link,
  CheckCircle,
  Copy,
  ExternalLink,
  Lock
} from 'lucide-react';
import { projectsAPI, signatureAPI } from '../services/api';
import AddProjectDialog from '../components/AddProjectDialog';
import EditProjectDialog from '../components/EditProjectDialog';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const Projects = () => {
  const { canCreate, canEdit, canDelete, isReadOnly } = useAuth();
  const [projectsData, setProjectsData] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [signatureLinkDialogOpen, setSignatureLinkDialogOpen] = useState(false);
  const [signatureLink, setSignatureLink] = useState('');
  const [generatingLink, setGeneratingLink] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchProjects();
  }, [selectedStatus]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const [projectsRes, statsRes] = await Promise.all([
        projectsAPI.getAll({ status: selectedStatus }),
        projectsAPI.getStats()
      ]);
      setProjectsData(projectsRes.data);
      setStats(statsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (projectId, projectName) => {
    try {
      await projectsAPI.delete(projectId);
      toast({
        title: "Gelukt",
        description: `Project "${projectName}" succesvol verwijderd`
      });
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast({
        title: "Fout",
        description: "Kan project niet verwijderen",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (project) => {
    setSelectedProject(project);
    setEditDialogOpen(true);
  };

  const handleGenerateSignatureLink = async (project) => {
    setGeneratingLink(true);
    try {
      const response = await signatureAPI.generateLink(project.id);
      const baseUrl = window.location.origin;
      const fullLink = `${baseUrl}/sign/${response.data.token}`;
      setSignatureLink(fullLink);
      setSelectedProject(project);
      setSignatureLinkDialogOpen(true);
      fetchProjects();
    } catch (error) {
      toast({
        title: "Fout",
        description: "Kan handtekeninglink niet genereren",
        variant: "destructive"
      });
    } finally {
      setGeneratingLink(false);
    }
  };

  const copySignatureLink = () => {
    navigator.clipboard.writeText(signatureLink);
    toast({
      title: "Gekopieerd!",
      description: "Handtekeninglink gekopieerd naar klembord"
    });
  };

  const getStatusLabel = (status) => {
    const labels = {
      'all': 'Alle Statussen',
      'quote': 'Offerte',
      'pending': 'In Afwachting',
      'confirmed': 'Bevestigd',
      'in-progress': 'In Uitvoering',
      'completed': 'Afgerond'
    };
    return labels[status] || status;
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      'low': 'Laag',
      'medium': 'Gemiddeld',
      'high': 'Hoog'
    };
    return labels[priority] || priority;
  };

  const statuses = ['all', 'quote', 'pending', 'confirmed', 'in-progress', 'completed'];

  const filteredProjects = projectsData.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.customer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalValue = stats?.totalValue || 0;
  const activeProjects = stats?.activeProjects || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Projecten laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Projecten</h1>
          <p className="text-gray-500 mt-1">Beheer verhuurprojecten en boekingen</p>
        </div>
        <div className="flex space-x-3">
          {canCreate() ? (
            <AddProjectDialog onSuccess={fetchProjects} />
          ) : (
            <div className="flex items-center text-gray-500 text-sm">
              <Lock className="w-4 h-4 mr-1" />
              Alleen lezen
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Totale Projectwaarde</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">€{totalValue.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Actieve Projecten</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{activeProjects}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                <FolderOpen className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Totaal Projecten</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{projectsData.length}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center shadow-lg">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-lg border-0">
        <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-blue-50">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center text-lg">
              <FolderOpen className="w-5 h-5 mr-2 text-purple-600" />
              Alle Projecten
            </CardTitle>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Zoek projecten..."
                  className="pl-10 w-64"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-projects-input"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="flex border-b bg-gray-50 p-4 space-x-2 overflow-x-auto">
            {statuses.map(status => (
              <Button
                key={status}
                variant={selectedStatus === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedStatus(status)}
                className={selectedStatus === status ? 'bg-gradient-to-r from-purple-600 to-blue-600' : ''}
              >
                {getStatusLabel(status)}
              </Button>
            ))}
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Projectnaam</TableHead>
                <TableHead className="font-semibold">Klant</TableHead>
                <TableHead className="font-semibold">Startdatum</TableHead>
                <TableHead className="font-semibold">Einddatum</TableHead>
                <TableHead className="font-semibold">Locatie</TableHead>
                <TableHead className="font-semibold">Apparatuur</TableHead>
                <TableHead className="font-semibold">Personeel</TableHead>
                <TableHead className="font-semibold">Waarde</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Prioriteit</TableHead>
                <TableHead className="font-semibold">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProjects.map((project) => (
                <TableRow key={project.id} className="hover:bg-gray-50 transition-colors duration-200">
                  <TableCell className="font-medium">{project.name}</TableCell>
                  <TableCell className="text-sm text-gray-600">{project.customer}</TableCell>
                  <TableCell className="text-sm text-gray-600">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                      {new Date(project.startDate).toLocaleDateString('nl-NL')}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {new Date(project.endDate).toLocaleDateString('nl-NL')}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">{project.location}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      {project.equipmentCount} items
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                      {project.crewCount} leden
                    </Badge>
                  </TableCell>
                  <TableCell className="font-semibold">€{project.totalValue.toLocaleString()}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Badge className={`${
                        project.status === 'confirmed' ? 'bg-green-100 text-green-700 hover:bg-green-100' :
                        project.status === 'in-progress' ? 'bg-blue-100 text-blue-700 hover:bg-blue-100' :
                        project.status === 'pending' ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100' :
                        project.status === 'quote' ? 'bg-gray-100 text-gray-700 hover:bg-gray-100' :
                        'bg-purple-100 text-purple-700 hover:bg-purple-100'
                      }`}>
                        {getStatusLabel(project.status)}
                      </Badge>
                      {project.signatureStatus === 'signed' && (
                        <CheckCircle className="w-4 h-4 text-green-600" title="Getekend" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`${
                      project.priority === 'high' ? 'bg-red-50 text-red-700 border-red-200' :
                      project.priority === 'medium' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                      'bg-green-50 text-green-700 border-green-200'
                    }`}>
                      {getPriorityLabel(project.priority)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-1">
                      {(project.status === 'quote' || project.status === 'pending') && project.signatureStatus !== 'signed' && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => handleGenerateSignatureLink(project)}
                          disabled={generatingLink}
                          title="Genereer handtekeninglink"
                          data-testid={`signature-${project.id}-btn`}
                        >
                          <PenTool className="w-3 h-3" />
                        </Button>
                      )}
                      {project.signatureStatus === 'pending' && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => {
                            const baseUrl = window.location.origin;
                            setSignatureLink(`${baseUrl}/sign/${project.signatureToken}`);
                            setSelectedProject(project);
                            setSignatureLinkDialogOpen(true);
                          }}
                          title="Bekijk handtekeninglink"
                          data-testid={`view-signature-link-${project.id}-btn`}
                        >
                          <Link className="w-3 h-3" />
                        </Button>
                      )}
                      {canEdit() && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-8"
                          onClick={() => handleEdit(project)}
                          data-testid={`edit-project-${project.id}-btn`}
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                      )}
                      {canDelete() && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-project-${project.id}-btn`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Project Verwijderen</AlertDialogTitle>
                              <AlertDialogDescription>
                                Weet je zeker dat je "{project.name}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuleren</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(project.id, project.name)}
                                className="bg-red-600 hover:bg-red-700"
                                data-testid="confirm-delete-project-btn"
                              >
                                Verwijderen
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <EditProjectDialog
        project={selectedProject}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSuccess={fetchProjects}
      />

      <Dialog open={signatureLinkDialogOpen} onOpenChange={setSignatureLinkDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <PenTool className="w-5 h-5 mr-2 text-green-600" />
              Digitale Handtekeninglink
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Deel deze link met <strong>{selectedProject?.customer}</strong> om hun digitale handtekening te verkrijgen voor de offerte "{selectedProject?.name}".
            </p>
            
            <div className="bg-gray-50 rounded-lg p-3 border">
              <p className="text-xs text-gray-500 mb-1">Handtekeninglink:</p>
              <div className="flex items-center space-x-2">
                <Input 
                  value={signatureLink} 
                  readOnly 
                  className="text-sm bg-white"
                />
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={copySignatureLink}
                  className="shrink-0"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button 
                variant="outline" 
                onClick={() => setSignatureLinkDialogOpen(false)}
              >
                Sluiten
              </Button>
              <Button 
                onClick={() => window.open(signatureLink, '_blank')}
                className="bg-green-600 hover:bg-green-700"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Link
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projects;
